import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNil, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Components
import { SkeletonLoader, Tooltip, Icon, Popover } from '@geneui/components';
import { SegmentModal } from 'components/SegmentModal';
import AccessControl from 'components/common/AccessControl';
import BonusModal from 'components/BonusModal';
import RecurrenceList from 'components/common/RecurrenceList';
import TemplateInfoModal from 'components/TemplateModal/TemplateInfoModal';
import SectionWithTitle from 'components/common/SectionWithTitle';
import BudgetLimitTypeKeyValue from 'components/Campaign/BudgetLimitTypePopover';
import InfoValueWithPopover from 'components/common/InfoValueWithPopover';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Helpers
import { customMomentWithoutTimezoneConversion, getPreCalculationResultText } from 'services/Helpers';
// Import Constants
import { l, TemplateTypesLabels, TimeZonePack } from 'constants/common';
// Import Enums
import { PermissionEnum } from 'enums';

const CampaignGeneralDetails = (props) => {
    const { campaignData, templateData, schedule, segmentData, bonus, goal, hasGoal, recurrenceData } = props;
    const { t } = useTranslation();

    const { partnerDateFormat, partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const hasBonus = !isNil(campaignData?.BonusId);
    const hasTemplate = !isNil(campaignData?.TemplateId);

    const [isVisibleSegmentInfo, setIsVisibleSegmentInfo] = useState(false);
    const [isVisibleTemplateInfo, setIsVisibleTemplateInfo] = useState(false);
    const [isVisibleSBonusInfo, setIsVisibleBonusInfo] = useState(false);

    const getContent = (data) => {
        return isNil(data) ? t(l.NA) : data;
    };

    const openSegmentModal = () => {
        setIsVisibleSegmentInfo(true);
    };

    const openTemplateModal = () => {
        setIsVisibleTemplateInfo(true);
    };

    const openBonusModal = () => {
        setIsVisibleBonusInfo(true);
    };

    return (
        <>
            <SectionWithTitle title={t(l.GeneralDetails)} hasContentPadding={false} hasFullHeight={true}>
                <div className="general-details-content">
                    <div className="general-details-column">
                        {!isNil(schedule) ? (
                            <>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.StartDate)}</p>
                                    <SkeletonLoader isBusy={isNil(schedule?.StartDate)}>
                                        {!isNil(schedule?.StartDate) && (
                                            <p className="g-details-subtitle">
                                                {isEmpty(schedule?.CronTime)
                                                    ? customMomentWithoutTimezoneConversion(schedule?.StartDate).format(
                                                          partnerDateTimeWithoutSecondsFormat,
                                                      )
                                                    : customMomentWithoutTimezoneConversion(schedule?.StartDate).format(
                                                          partnerDateFormat,
                                                      )}
                                            </p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.EndDate)}</p>
                                    <SkeletonLoader isBusy={isNil(schedule?.EndDate)}>
                                        {!isNil(schedule?.EndDate) && (
                                            <p className="g-details-subtitle">
                                                {isEmpty(schedule?.CronTime)
                                                    ? customMomentWithoutTimezoneConversion(schedule?.EndDate).format(
                                                          partnerDateTimeWithoutSecondsFormat,
                                                      )
                                                    : customMomentWithoutTimezoneConversion(schedule?.EndDate).format(
                                                          partnerDateFormat,
                                                      )}
                                            </p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                                {!isEmpty(schedule?.CronTime) && (
                                    <>
                                        <div className="general-details-column-info">
                                            <p className="g-details-title">
                                                {t(l.ExecutionTime)}
                                                <Tooltip text={schedule?.CronSummary} position="top">
                                                    <Icon type="bc-icon-info" />
                                                </Tooltip>
                                            </p>
                                            <SkeletonLoader isBusy={isNil(schedule?.CronTime)}>
                                                {!isNil(schedule?.CronTime) && (
                                                    <>
                                                        <p className="g-details-subtitle">{schedule?.CronTime}</p>
                                                    </>
                                                )}
                                            </SkeletonLoader>
                                        </div>
                                        <div className="general-details-column-info">
                                            <p className="g-details-title">{t(l.ScheduleType)}</p>
                                            <SkeletonLoader isBusy={isNil(schedule?.ScheduleType)}>
                                                {!isNil(schedule?.ScheduleType) && (
                                                    <p className="g-details-subtitle">{t(schedule?.ScheduleType)}</p>
                                                )}
                                            </SkeletonLoader>
                                        </div>
                                        <div className="general-details-column-info">
                                            <p className="g-details-title">{t(l.RecurrenceCount)}</p>
                                            <SkeletonLoader isBusy={isNil(recurrenceData)}>
                                                {!isNil(recurrenceData) && (
                                                    <Popover
                                                        className="next-run-date-popover-container"
                                                        extendTargetWidth={false}
                                                        Content={
                                                            <RecurrenceList
                                                                upcomingExecutions={recurrenceData?.UpcomingExecutions}
                                                            />
                                                        }
                                                        align="end"
                                                        position="right"
                                                    >
                                                        <button
                                                            disabled={recurrenceData?.Count === 0}
                                                            className="blue-color-button"
                                                        >
                                                            {recurrenceData?.Count}
                                                        </button>
                                                    </Popover>
                                                )}
                                            </SkeletonLoader>
                                        </div>
                                    </>
                                )}
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.TimeZone)}</p>
                                    <SkeletonLoader isBusy={isNil(schedule?.TimeZone)}>
                                        {!isNil(schedule?.TimeZone) && (
                                            <p className="g-details-subtitle">{TimeZonePack[schedule?.TimeZone]}</p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.PreCalculationSchedule)}</p>
                                    <p className="g-details-subtitle">
                                        {getPreCalculationResultText(t, campaignData.PreCalculationHours)}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <div className="general-details-column-info">
                                <p className="g-details-title">{t(l.Schedule)}</p>
                                <SkeletonLoader isBusy={isNil(campaignData)}>
                                    {!isNil(campaignData) && (
                                        <p className="g-details-subtitle">{t(l.ScheduleOffImmediately)}</p>
                                    )}
                                </SkeletonLoader>
                            </div>
                        )}
                        {hasGoal && (
                            <div className="general-details-column-info">
                                <p className="g-details-title">{t(l.Goal)}</p>
                                <SkeletonLoader isBusy={isNil(goal?.Name)}>
                                    {!isNil(goal?.Name) && <p className="g-details-subtitle">{goal.Name}</p>}
                                </SkeletonLoader>
                            </div>
                        )}
                    </div>
                    <div className="general-details-column">
                        <div className="general-details-column-info">
                            <p className="g-details-title">{t(l.AttachedSegment)}</p>
                            <SkeletonLoader isBusy={isNil(segmentData?.Name)}>
                                {!isNil(segmentData) && (
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                    <p
                                        className={`g-details-subtitle ${!isNil(segmentData?.Name) && 'active'}`}
                                        onClick={!isNil(segmentData?.Name) ? openSegmentModal : noop}
                                    >
                                        {getContent(segmentData.Name)}
                                    </p>
                                )}
                            </SkeletonLoader>
                        </div>
                        {hasTemplate && (
                            <div className="general-details-column-info">
                                <p className="g-details-title">{t(l.AttachedTemplate)}</p>
                                <SkeletonLoader isBusy={isNil(templateData)}>
                                    {!isNil(templateData) && (
                                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                        <p
                                            className={`g-details-subtitle ${!isNil(templateData?.Name) && 'active'}`}
                                            onClick={!isNil(templateData?.Name) ? openTemplateModal : noop}
                                        >
                                            {getContent(templateData.Name)}
                                        </p>
                                    )}
                                </SkeletonLoader>
                            </div>
                        )}
                        {!isEmpty(campaignData.ExclusionRules) && (
                            <div className="general-details-column-info">
                                <p className="g-details-title">{t(l.ExclusionRules)}</p>
                                <InfoValueWithPopover
                                    data={campaignData.ExclusionRules}
                                    labelKey="Name"
                                    IdKey="ExclusionRuleId"
                                />
                            </div>
                        )}
                        {hasTemplate && (
                            <>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.ProviderName)}</p>
                                    <SkeletonLoader isBusy={isNil(campaignData)}>
                                        {!isNil(campaignData) && (
                                            <p className="g-details-subtitle">
                                                {getContent(campaignData.MessageProvider?.Name)}
                                            </p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.Channel)}</p>
                                    <SkeletonLoader isBusy={isNil(templateData)}>
                                        {!isNil(templateData) && (
                                            <p className="g-details-subtitle">
                                                {getContent(TemplateTypesLabels[templateData.DeliveryMethod])}
                                            </p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                            </>
                        )}
                        {hasBonus && (
                            <AccessControl permissions={PermissionEnum.ViewAwards}>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.AttachedBonus)}</p>
                                    <SkeletonLoader isBusy={isNil(bonus)}>
                                        {!isNil(bonus) && (
                                            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                            <p
                                                className={`g-details-subtitle ${!isNil(bonus?.Name) && 'active'}`}
                                                onClick={!isNil(bonus?.Name) ? openBonusModal : noop}
                                            >
                                                {getContent(bonus?.Name)}
                                            </p>
                                        )}
                                    </SkeletonLoader>
                                </div>
                            </AccessControl>
                        )}
                        {hasGoal && (
                            <div className="general-details-column-info">
                                <p className="g-details-title">{t(l.GoalProgress)}</p>
                                <SkeletonLoader isBusy={isNil(goal?.GoalValue)}>
                                    {!isNil(goal?.GoalValue) && (
                                        <p className="g-details-subtitle">
                                            {+goal.GoalValue !== 0
                                                ? Math.ceil((goal.GoalReachedClientCount / goal.GoalValue) * 100)
                                                : 0}
                                            {' %'}
                                        </p>
                                    )}
                                </SkeletonLoader>
                            </div>
                        )}
                        {hasBonus && (
                            <div className="general-details-column-info">
                                <BudgetLimitTypeKeyValue savedBudget={campaignData?.Budget} />
                            </div>
                        )}
                    </div>
                </div>
            </SectionWithTitle>
            {isVisibleSegmentInfo && (
                <SegmentModal
                    targetObject={segmentData?.TargetObject}
                    segmentId={segmentData?.SegmentId}
                    segmentType={segmentData?.Type}
                    handleClose={() => setIsVisibleSegmentInfo(false)}
                    isVisible={isVisibleSegmentInfo}
                />
            )}
            {isVisibleTemplateInfo && (
                <TemplateInfoModal
                    templateId={templateData?.TemplateId}
                    handleClose={() => setIsVisibleTemplateInfo(false)}
                    isVisible={isVisibleTemplateInfo}
                />
            )}
            {isVisibleSBonusInfo && (
                <BonusModal
                    bonusId={bonus?.Id}
                    isVisible={isVisibleSBonusInfo}
                    handleClose={() => setIsVisibleBonusInfo(false)}
                />
            )}
        </>
    );
};

CampaignGeneralDetails.displayName = 'CampaignGeneralDetails';

CampaignGeneralDetails.propTypes = {
    schedule: PropTypes.object,
    segmentData: PropTypes.object,
    campaignData: PropTypes.object,
    templateData: PropTypes.object,
    bonus: PropTypes.object,
    goal: PropTypes.object,
    hasGoal: PropTypes.bool.isRequired,
    recurrenceData: PropTypes.object,
};

export default memo(CampaignGeneralDetails);
